import React, { useEffect, useId, useState } from 'react';
import { useBundleBaselineUpdate } from '../../hooks/useBundleBaselineUpdate';
import { useBundleFloorLevel } from '../../hooks/useBundleFloorLevel';
import { parseWholeNumberInput } from '../orders/orderdetail/bundleWorkOrderLogic';
import SuccessFlash, { useSuccessFlash } from './SuccessFlash';

interface ProductInfoBaselineSectionProps {
  readonly sku: string;
}

/**
 * Change Baseline for a bundle parent with a pick bin, same PATCH and validation as the
 * bundle work order Change Baseline. Renders nothing for any other SKU (floor GET 404s).
 */
function ProductInfoBaselineSection({ sku }: ProductInfoBaselineSectionProps): React.ReactElement | null {
  const inputId = useId();
  const { floor, applyFloorUpdate } = useBundleFloorLevel(sku);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const successFlash = useSuccessFlash();
  const baselineUpdate = useBundleBaselineUpdate(sku);

  useEffect(() => {
    setEditing(false);
    setLocalError(null);
  }, [sku]);

  if (floor === null) return null;
  const baselineQty = floor.targetQty;

  const handleStartEdit = (): void => {
    setValue(String(baselineQty));
    setLocalError(null);
    baselineUpdate.clearError();
    setEditing(true);
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const parsed = parseWholeNumberInput(value);
    if (!parsed.ok) {
      setLocalError(parsed.error);
      return;
    }
    setLocalError(null);
    const updated = await baselineUpdate.submit(parsed.qty);
    if (!updated) return;
    applyFloorUpdate(updated);
    setEditing(false);
    successFlash.flash();
  };

  const displayError = localError ?? baselineUpdate.error;

  return (
    <div className="product-info-notes product-info-baseline">
      <div className="product-info-notes-title">Change Baseline</div>
      {editing ? (
        <form onSubmit={(event) => { void handleSubmit(event); }}>
          <label className="product-info-baseline-label" htmlFor={inputId}>
            Baseline
          </label>
          <input
            id={inputId}
            className="product-info-note-input"
            inputMode="numeric"
            autoComplete="off"
            value={value}
            disabled={baselineUpdate.submitting}
            onChange={(event) => setValue(event.target.value)}
            onFocus={(event) => event.target.select()}
            autoFocus
          />
          {displayError ? (
            <div className="product-info-weight-save-error" role="alert">{displayError}</div>
          ) : null}
          <div className="product-info-note-btn-wrapper product-info-note-btn-wrapper-row">
            <button
              type="button"
              className="product-info-note-btn product-info-note-btn-add"
              onClick={() => setEditing(false)}
              disabled={baselineUpdate.submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="product-info-note-btn product-info-note-btn-write active"
              disabled={baselineUpdate.submitting}
            >
              {baselineUpdate.submitting ? 'Saving…' : 'Update'}
            </button>
          </div>
        </form>
      ) : (
        <>
          <div className="product-info-note-text">Baseline: {baselineQty}</div>
          <div className="product-info-note-btn-wrapper">
            <button
              type="button"
              className="product-info-note-btn product-info-note-btn-add"
              onClick={handleStartEdit}
            >
              Change Baseline
            </button>
          </div>
        </>
      )}
      <SuccessFlash visible={successFlash.visible} />
    </div>
  );
}

export default ProductInfoBaselineSection;
